import QRCodeStyling from 'qr-code-styling'

/**
 * The Meiuca-branded QR model — a single source of truth shared by every surface
 * that shows a QR: the arena promo-art tool (`/tools/qr-art`) and the tournament
 * event follow-along link. Square dark modules with the Meiuca symbol on a white
 * disc in the center, high error correction so the center image is tolerated.
 */

/** Brand dark used for the QR modules, the center symbol and dark text. */
export const MEIUCA_DARK = '#111827'

/** Meiuca twin-stroke symbol paths (within a 0–100 viewBox). */
export const MEIUCA_SYMBOL_PATHS = [
  'M 12 84 L 12 22 Q 12 14 19 19 L 44 60 Q 46 63 46 67 L 46 80 Q 46 84 42 84 Z',
  'M 88 84 L 88 22 Q 88 14 81 19 L 56 60 Q 54 63 54 67 L 54 80 Q 54 84 58 84 Z',
]

/** Dark symbol on a white disc — the centered image inside the QR code. */
export const MEIUCA_SYMBOL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50" fill="#ffffff"/><path d="${MEIUCA_SYMBOL_PATHS[0]}" fill="${MEIUCA_DARK}"/><path d="${MEIUCA_SYMBOL_PATHS[1]}" fill="${MEIUCA_DARK}"/></svg>`

const svgToDataUrl = (svg: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`

/** Builds the `qr-code-styling` options for a branded QR of `data` at `size` px. */
export function buildBrandedQrOptions(data: string, size: number) {
  return {
    width: size,
    height: size,
    type: 'canvas' as const,
    data,
    image: svgToDataUrl(MEIUCA_SYMBOL_SVG),
    margin: Math.round(size * 0.016),
    qrOptions: { errorCorrectionLevel: 'H' as const },
    dotsOptions: { type: 'square' as const, color: MEIUCA_DARK },
    cornersSquareOptions: { type: 'square' as const, color: MEIUCA_DARK },
    cornersDotOptions: { type: 'square' as const, color: MEIUCA_DARK },
    backgroundOptions: { color: '#FFFFFF' },
    imageOptions: { crossOrigin: 'anonymous', margin: Math.round(size * 0.008), imageSize: 0.28 },
  }
}

/** Renders a high-resolution branded QR of `data` and triggers a PNG download. */
export async function downloadBrandedQrPng(data: string, size: number, filename: string): Promise<void> {
  const qr = new QRCodeStyling(buildBrandedQrOptions(data, size))
  const raw = await qr.getRawData('png')
  if (!raw) return
  const blob = raw instanceof Blob ? raw : new Blob([raw as BlobPart], { type: 'image/png' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename.endsWith('.png') ? filename : `${filename}.png`
  a.click()
  URL.revokeObjectURL(a.href)
}
